import * as React from 'react';
import { Provider as ReduxProvider } from 'react-redux';
import type { Middleware } from 'redux';
import { isWrmOptimizationsEnabled } from '@helpcenter/feature-flags';
import { memoize, keys } from 'lodash';
import { IntlProvider as IntlProviderNext } from 'react-intl';
import { RelayEnvironmentProvider } from 'react-relay';
import type { State } from 'state';
import { UPDATE_USER_MODEL } from 'state/actions/user';
import createStore from 'state/create-store';
import { getLocale } from 'state/selectors/user';
import { HelpSeekerTypeProvider } from 'utils/analytics/help-seeker-type-provider';
import { EngagementProvider } from 'view/common/engagement-provider';
import { FeatureFlagProvider } from 'view/common/feature-flag';
import { TFECacheProvider } from 'view/common/forge-ui/tfe-cache-provider';
import { CustomiseSidebarTriggerProvider } from 'view/common/pushing-customize-sidebar';
import type { AnalyticsWebClient } from '@atlaskit/analytics-listeners/types';
import FabricAnalyticsListeners from '@atlaskit/analytics-listeners/FabricAnalyticsListeners';
import { FabricChannel } from '@atlaskit/analytics-listeners/types';
import type UIAnalyticsEvent from '@atlaskit/analytics-next/UIAnalyticsEvent';
import AnalyticsListener from '@atlaskit/analytics-next/AnalyticsListener';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { ANALYTICS_BRIDGE_CHANNEL } from '@atlassian/analytics-bridge/constants';
import { setAccountMetaTag } from '@atlassian/help-center-common-util/account';
import { sendEvent, sendAtlaskitUIEvent } from '@atlassian/help-center-common-util/analytics';
import { getAnalyticsWebClient } from '@atlassian/help-center-common-util/analytics/client';
import { CustomDomainsAnalyticsProvider } from '@atlassian/help-center-common-util/analytics/custom-domains-analytics-provider';
import { FeatureFlagAnalyticsContext } from '@atlassian/help-center-common-util/analytics/feature-flag-context';
import { sendEventFromAnalyticsBridge } from '@atlassian/help-center-common-util/analytics/listener';
import { PortalAnalyticsContext } from '@atlassian/help-center-common-util/analytics/portal-context';
import { CoordinationClientProvider } from '@atlassian/help-center-common-util/coordination-client-provider';
import {
	getMessages,
	onIntlError as sharedOnIntlError,
	onIntlNextError as sharedOnIntlNextError,
} from '@atlassian/help-center-common-util/i18n-messages';
import { getLanguageFromLocale } from '@atlassian/help-center-common-util/locale-data';
import { getRelayEnvironment } from '@atlassian/help-center-common-util/relay';
import { DeferredWrmResourceProvider } from '@atlassian/help-center-common-util/wrm';
import { MetaDataProvider } from './view/meta-data-provider';
import type { AtlassianHelpConfig } from 'types';

const initialState: State = {} as State;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const accountTypeMetaTagMiddleware: Middleware = () => (next) => (action: any) => {
	// TypeScript upgrade (v4.4.3). Please correct when you revisit this code.
	// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
	if (action.type === UPDATE_USER_MODEL) {
		// TypeScript upgrade (v4.4.3). Please correct when you revisit this code.
		// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
		setAccountMetaTag(action.payload.atlassianAccountManaged, action.payload.accountId);
	}
	// Suppressing existing violation. Please fix this.
	// eslint-disable-next-line @typescript-eslint/no-unsafe-return
	return next(action);
};

const { store, injectReducer } = createStore({
	initialState,
	middleware: [accountTypeMetaTagMiddleware],
});

export interface Props {
	config: AtlassianHelpConfig;
}

/**
 * Re-exported from `@atlassian/help-center-common-util/i18n-messages` to keep
 * existing `import { onIntlError, onIntlNextError } from 'providers'` consumers
 * working. New consumers should import directly from the util package.
 */
export const onIntlError = sharedOnIntlError;
export const onIntlNextError = sharedOnIntlNextError;

const onAnalyticsEvent = (event: UIAnalyticsEvent) => {
	if (FeatureGates.checkGate('enable_help_center_analytics_bridge')) {
		sendEventFromAnalyticsBridge(event);
	} else {
		sendEvent(event);
	}
};

export class AppProviders extends React.Component<React.PropsWithChildren<Props>> {
	render() {
		const { config } = this.props;
		const analyticsClient = getAnalyticsWebClient().getInstance() as unknown as AnalyticsWebClient;

		const envFeatureFlags = (store.getState() as State).persisted.env.featureFlags;
		const configFeatureFlags = config?.featureFlags;
		const memoizedFeatureFlags = memoize(
			// Suppressing existing violation. Please fix this.
			// eslint-disable-next-line @typescript-eslint/no-unsafe-return
			(envFFs, configFFs) => ({
				...envFFs,
				...configFFs,
			}),
			(...args) =>
				keys({
					...args[0],
					...args[1],
				}),
		);
		const WrmResourceProvider = isWrmOptimizationsEnabled()
			? DeferredWrmResourceProvider
			: React.Fragment;
		return (
			<RelayEnvironmentProvider environment={getRelayEnvironment()}>
				<CustomDomainsAnalyticsProvider baseUrl={config.baseUrl}>
					<FabricAnalyticsListeners
						client={analyticsClient}
						excludedChannels={[FabricChannel.atlaskit]}
					>
						<EngagementProvider config={config} analyticsClientInstance={analyticsClient}>
							<AnalyticsListener onEvent={sendAtlaskitUIEvent} channel="atlaskit">
								<AnalyticsListener
									onEvent={sendEventFromAnalyticsBridge}
									channel={ANALYTICS_BRIDGE_CHANNEL}
								>
									<AnalyticsListener onEvent={onAnalyticsEvent}>
										{/* @ts-ignore */}
										<IntlProviderNext
											messages={getMessages()}
											locale={getLanguageFromLocale(getLocale(store.getState() as State))}
											onError={onIntlNextError}
										>
											{/* Suppressing existing violation. Please fix this. */}
											{/* eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment */}
											<ReduxProvider store={store as any}>
												<MetaDataProvider config={this.props.config}>
													<WrmResourceProvider>
														<PortalAnalyticsContext>
															<HelpSeekerTypeProvider>
																<FeatureFlagProvider
																	// Suppressing existing violation. Please fix this.
																	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
																	featureFlags={memoizedFeatureFlags(
																		envFeatureFlags,
																		configFeatureFlags,
																	)}
																>
																	<FeatureFlagAnalyticsContext>
																		<CoordinationClientProvider>
																			<TFECacheProvider>
																				<CustomiseSidebarTriggerProvider>
																					{this.props.children}
																				</CustomiseSidebarTriggerProvider>
																			</TFECacheProvider>
																		</CoordinationClientProvider>
																	</FeatureFlagAnalyticsContext>
																</FeatureFlagProvider>
															</HelpSeekerTypeProvider>
														</PortalAnalyticsContext>
													</WrmResourceProvider>
												</MetaDataProvider>
											</ReduxProvider>
										</IntlProviderNext>
									</AnalyticsListener>
								</AnalyticsListener>
							</AnalyticsListener>
						</EngagementProvider>
					</FabricAnalyticsListeners>
				</CustomDomainsAnalyticsProvider>
			</RelayEnvironmentProvider>
		);
	}
}

export { store, injectReducer };
